import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, ConformanceToGiasStandardAttestationField, ConformanceToGiasStandardAttestationInput, ConformanceToGiasStandardAttestationQueryComplexInput, ConformanceToGiasStandardAttestationSimpleQueryInput, CreateAndSignGiasAssessmentGQL, CreateAndSignGiasAssessmentMutation, ExportGiasAssessmentGQL, ExportGiasAssessmentQuery, GetGiasAssessmentGQL, GetGiasAssessmentQuery, OperatorInput, PagingInput } from "../../../graphql/generated";
import { map, Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class GiasAssessmentService {
    private readonly GetGiasAssessmentGQL: GetGiasAssessmentGQL = inject(GetGiasAssessmentGQL);
    private readonly CreateAndSignGiasAssessmentGQL: CreateAndSignGiasAssessmentGQL = inject(CreateAndSignGiasAssessmentGQL);
    private readonly ExportGiasAssessmentGQL: ExportGiasAssessmentGQL = inject(ExportGiasAssessmentGQL)

    getGiasAssessment(campaignId: string, attestationId: string | null, legalEntityIds: string[] | null, parameters: any): Observable<GetGiasAssessmentQuery['getConformanceGIASAttestation']> {
        const params = convertLazyLoadToComplex(parameters)
        return this.GetGiasAssessmentGQL.fetch({ campaignId, attestationId: attestationId ?? null, legalEntityIds: legalEntityIds ?? null, page: params.page, query: params.query }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getConformanceGIASAttestation))
    }

    createAndSignAssessment(campaignId: string, legalEntityTrackingIds: string[], input: ConformanceToGiasStandardAttestationInput): Observable<CreateAndSignGiasAssessmentMutation['createAndSignConformanceToGIASAttestation']> {
        return this.CreateAndSignGiasAssessmentGQL.mutate({ campaignId, input, legalEntityTrackingIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createAndSignConformanceToGIASAttestation))
    }

    exportGiasAssessment(campaignId: string, attestationId: string, legalEntityIds: string[] | null, timeZone: string, parameters: any): Observable<ExportGiasAssessmentQuery['downloadConformanceGIASAttestationExcel']> {
        const params = convertLazyLoadToComplex(parameters)
        return this.ExportGiasAssessmentGQL.fetch({ attestationId, campaignId, legalEntityIds: legalEntityIds ?? null, query: params.query, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.downloadConformanceGIASAttestationExcel))
    }
}

export function convertLazyLoadToComplex(parameters: any): { query: ConformanceToGiasStandardAttestationQueryComplexInput, page: PagingInput } {
    const query: ConformanceToGiasStandardAttestationSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_MANAGE_CAMPAIGN_CONFORMANCE_GIAS[field as keyof typeof ENUM_MANAGE_CAMPAIGN_CONFORMANCE_GIAS]).forEach(key => {

                        query.push({
                            field: key as ConformanceToGiasStandardAttestationField,
                            filters: {
                                operator: [ConformanceToGiasStandardAttestationField.RatingQuantitative].includes(key) ? ComparatorOperators.Eq : ComparatorOperators.Match,
                                value: values?.map(v => String(v)?.toLowerCase() === 'empty' ? null : key === ConformanceToGiasStandardAttestationField.RatingQualitative ? String(v)?.replace(' ', '_') : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 20
        }
    }
}

export const ENUM_MANAGE_CAMPAIGN_CONFORMANCE_GIAS = {
    domain: [ConformanceToGiasStandardAttestationField.Domain],
    standard: [ConformanceToGiasStandardAttestationField.Standard],
    principle: [ConformanceToGiasStandardAttestationField.Principle],
    auditValueChain: [ConformanceToGiasStandardAttestationField.AuditValueChain],
    globalInternalAuditStandards: [ConformanceToGiasStandardAttestationField.GlobalInternalAuditStandard],
    exampleOfEvidenceOfConformance: [ConformanceToGiasStandardAttestationField.ExampleOfEvidenceOdConformance],
    gaMainReferences: [ConformanceToGiasStandardAttestationField.GaMainReferences],
    quantitativeRating: [ConformanceToGiasStandardAttestationField.RatingQuantitative],
    qualitativeRating: [ConformanceToGiasStandardAttestationField.RatingQualitative],
    assessmentDescriptionExplanation: [ConformanceToGiasStandardAttestationField.AssessmentDescriptionExplanations],
    supportingEvidences: [ConformanceToGiasStandardAttestationField.SupportingEvidence],
    actionPlanDescription: [ConformanceToGiasStandardAttestationField.ActionPlanDescription],
    actionPlanResponsible: [ConformanceToGiasStandardAttestationField.ActionPlanResponsible],
    actionPlanDeadline: [ConformanceToGiasStandardAttestationField.ActionPlanDeadline],
    status: [ConformanceToGiasStandardAttestationField.AttestationStatus]
}